import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    ArrowRight,
    Briefcase,
    Clock,
    GripVertical,
    ListTodo,
    Newspaper,
    Star,
} from 'lucide-react';
import type { Application, CatalogFilters as Filters } from '../types';
import {
    useCatalog,
    useCategories,
    useHome,
    useOpenApplication,
    useReorderFavorite,
    useToggleFavorite,
} from '../hooks/useCatalog';
import { clinDOeil, dateEnClair, motSurLeVide, salutationComplete } from '../lib/accueil';
import { ApplicationCard } from '../components/ApplicationCard';
import { CatalogFilters } from '../components/CatalogFilters';
import { EmptyState, ErrorState, LoadingState } from '../components/Feedback';
import { AppIcon } from '../components/AppIcon';
import { ExternalTaskPanel } from '../components/ExternalTaskPanel';
import { WeatherIcon } from '../components/WeatherIcon';
import { useAuth } from '../contexts/AuthContext';
import { appColor, cn, formatDate } from '../lib/utils';

const STATUT_TACHE_PROJET: Record<'a_faire' | 'en_cours', string> = {
    a_faire: 'À faire',
    en_cours: 'En cours',
};

/* Le catalogue ne montre que les applications accessibles : une application
   sans droit n'existe pas pour l'utilisateur qui ne peut pas l'ouvrir, le
   serveur ne la renvoie même pas (voir backend/src/services/catalog.service.ts). */
const EMPTY_FILTERS: Filters = {
    search: '',
    category: null,
    environment: null,
    network: null,
    favoritesOnly: false,
};

export function CatalogPage() {
    const { user } = useAuth();
    const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);

    const { data: categories = [] } = useCategories();
    const { data, isLoading, isError, error, refetch } = useCatalog(filters);
    const { data: home } = useHome();

    const toggleFavorite = useToggleFavorite();
    const reorderFavorite = useReorderFavorite();
    const openApplication = useOpenApplication();

    const isFiltered =
        filters.search !== '' ||
        filters.category !== null ||
        filters.environment !== null ||
        filters.network !== null ||
        filters.favoritesOnly;

    const applications = data?.applications ?? [];

    /* Le décompte du bonjour porte sur le catalogue entier, pas sur le résultat
       du moment : « /api/catalog » recalcule son compteur sur l'ensemble filtré,
       si bien qu'une recherche faisait fondre la phrase d'accueil à chaque
       frappe, jusqu'à annoncer zéro application à disposition. « /home » compte
       sans filtre, c'est lui qui fait foi ici. */
    const total = home?.counts.total ?? data?.counts.total ?? 0;

    const cartes = (liste: Application[]) => (
        <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {liste.map((application) => (
                <ApplicationCard
                    key={application.id}
                    application={application}
                    onOpen={openApplication}
                    onToggleFavorite={(app) => toggleFavorite.mutate(app)}
                />
            ))}
        </div>
    );

    /* L'heure est relue à chaque rendu plutôt que figée au montage : cette page
       reste ouverte des journées entières, et une salutation qui dit encore
       « Bonjour » à vingt heures se remarque tout de suite. */
    const maintenant = new Date();
    const mot = clinDOeil(maintenant);

    /* Raccourcis vers les autres applications du parc : toujours en tout haut
       de la page, avant même la salutation, filtre actif ou non. Ce sont des
       tâches personnelles qui attendent une action, pas une information sur
       le catalogue lui-même. Chaque carte ne s'affiche que si elle a quelque
       chose à montrer, comme le reste de l'accueil (favoris, veille…), et les
       deux se partagent la même rangée quand elles sont présentes toutes les
       deux, comme favoris/récentes plus bas. */
    const tachesProjet = home?.projetTasks ?? [];
    const avvEnCours = home?.avvOpportunities ?? [];
    const actualites = home?.announcements ?? [];

    /* Deux cartes au plus se partagent cette rangée (Actualités vit à part,
       dans le bandeau de droite) : une grille à deux colonnes suffit, remplie
       ou non selon ce qu'il y a à montrer. */
    const grilleRaccourcis = tachesProjet.length > 0 && avvEnCours.length > 0 ? 'grid gap-4 md:grid-cols-2' : '';

    return (
        <div className="flex flex-col lg:flex-row gap-6 items-start">
            <div className="flex-1 min-w-0 space-y-6">
                <header>
                    <div className="flex items-center gap-3">
                        <h1 className="text-2xl font-bold text-[var(--color-text)]">
                            {salutationComplete(maintenant, user?.firstName ?? user?.displayName)}
                        </h1>
                        {home?.weather && <WeatherIcon weather={home.weather} />}
                    </div>
                    <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
                        {total === 0
                            ? 'Aucune application n’est encore ouverte à votre compte.'
                            : `${total} application${total > 1 ? 's' : ''} à votre disposition.`}
                    </p>
                    {/* La date et le clin d'oeil partagent une ligne discrète sous
                        l'information de service. La date est toujours là, le clin
                        d'oeil presque jamais : le séparateur ne s'affiche donc que
                        lorsqu'il y a bien deux choses à séparer. */}
                    <p className="mt-1.5 text-xs text-[var(--color-text-muted)]">
                        {dateEnClair(maintenant)}
                        {mot && <span className="italic"> · {mot}</span>}
                    </p>
    
                    {/* L'actualité est absente quand la veille n'a rien de frais,
                        notamment si le pod n'a pas de sortie internet. L'accueil se
                        contente alors de la date, sans trou visible. */}
                    {home?.veille && (
                        <a
                            href={home.veille.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-3 inline-flex items-start gap-2 max-w-2xl group"
                        >
                            <Newspaper
                                className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[var(--color-text-muted)]"
                                aria-hidden
                            />
                            <span className="text-xs text-[var(--color-text-secondary)] group-hover:underline">
                                <span className="font-semibold">{home.veille.source}</span>
                                {' · '}
                                {home.veille.title}
                            </span>
                        </a>
                    )}
                </header>
    
                {(tachesProjet.length > 0 || avvEnCours.length > 0) && (
                    <section className={grilleRaccourcis}>
                        {tachesProjet.length > 0 && (
                            <ExternalTaskPanel
                                title="Projet · Tâches à faire"
                                icon={<ListTodo className="w-4 h-4" aria-hidden />}
                                items={tachesProjet.slice(0, 4).map((tache) => ({
                                    id: tache.id,
                                    label: tache.name,
                                    sublabel: tache.endDate
                                        ? `${tache.projectName} · Échéance le ${formatDate(tache.endDate)}`
                                        : tache.projectName,
                                    url: tache.url,
                                    badge: {
                                        text: STATUT_TACHE_PROJET[tache.status],
                                        className:
                                            tache.status === 'en_cours'
                                                ? 'shrink-0 text-xs font-medium px-2 py-0.5 rounded-full bg-[var(--color-primary)]/10 text-[var(--color-primary)]'
                                                : 'shrink-0 text-xs font-medium px-2 py-0.5 rounded-full bg-[var(--color-bg-hover)] text-[var(--color-text-secondary)]',
                                    },
                                }))}
                            />
                        )}
                        {avvEnCours.length > 0 && (
                            <ExternalTaskPanel
                                title="AVV · En cours"
                                icon={<Briefcase className="w-4 h-4" aria-hidden />}
                                items={avvEnCours.slice(0, 4).map((avv) => ({
                                    id: avv.id,
                                    label: `${avv.client} — ${avv.name}`,
                                    sublabel: avv.restitutionDate
                                        ? `Restitution le ${formatDate(avv.restitutionDate)}`
                                        : null,
                                    url: avv.url,
                                    badge: avv.statusColor
                                        ? {
                                              text: avv.statusValue,
                                              className: 'shrink-0 text-xs font-medium px-2 py-0.5 rounded-full',
                                              style: {
                                                  backgroundColor: `${avv.statusColor}1a`,
                                                  color: avv.statusColor,
                                              },
                                          }
                                        : {
                                              text: avv.statusValue,
                                              className:
                                                  'shrink-0 text-xs font-medium px-2 py-0.5 rounded-full bg-[var(--color-bg-hover)] text-[var(--color-text-secondary)]',
                                          },
                                }))}
                            />
                        )}
                    </section>
                )}
    
                {/* Les raccourcis ne s'affichent que sur la vue non filtrée : dès que
                    l'utilisateur cherche quelque chose, seuls les résultats comptent. */}
                {!isFiltered && home && (home.favorites.length > 0 || home.recent.length > 0) && (
                    <section className="grid gap-4 md:grid-cols-2">
                        {home.favorites.length > 0 && (
                            <ShortcutPanel
                                title="Vos favoris"
                                icon={<Star className="w-4 h-4" aria-hidden />}
                                applications={home.favorites.slice(0, 5)}
                                onOpen={openApplication}
                                reorderable
                                onReorder={(applicationId, beforeApplicationId) =>
                                    reorderFavorite.mutate({ applicationId, beforeApplicationId })
                                }
                            />
                        )}
                        {home.recent.length > 0 && (
                            <ShortcutPanel
                                title="Récemment ouvertes"
                                icon={<Clock className="w-4 h-4" aria-hidden />}
                                applications={home.recent.slice(0, 5)}
                                onOpen={openApplication}
                            />
                        )}
                    </section>
                )}
    
                <CatalogFilters
                    filters={filters}
                    categories={categories}
                    resultCount={applications.length}
                    onChange={setFilters}
                />
    
                {isLoading ? (
                    <LoadingState label="Chargement du catalogue…" />
                ) : isError ? (
                    <ErrorState
                        message={error instanceof Error ? error.message : 'Catalogue indisponible.'}
                        onRetry={() => void refetch()}
                    />
                ) : applications.length === 0 ? (
                    <EmptyState
                        title="Aucune application ne correspond"
                        description={`${motSurLeVide(maintenant)} Essayez d’élargir votre recherche ou de retirer un filtre.`}
                        action={
                            <button
                                type="button"
                                onClick={() => setFilters(EMPTY_FILTERS)}
                                className="btn btn-secondary"
                            >
                                Réinitialiser les filtres
                            </button>
                        }
                    />
                ) : (
                    cartes(applications)
                )}
            </div>

            {/* Les actualités vivent à part, dans un bandeau qui occupe toute la
                hauteur disponible plutôt qu'une carte parmi d'autres : plus de
                messages visibles d'un coup, sans faire grandir le reste de la
                page. Il défile lui-même si la liste dépasse l'écran. */}
            {actualites.length > 0 && (
                <aside className="w-full lg:w-72 shrink-0 lg:sticky lg:top-24">
                    <ExternalTaskPanel
                        title="Actualités"
                        icon={<Newspaper className="w-4 h-4" aria-hidden />}
                        className="lg:h-[calc(100vh-7rem)]"
                        dense
                        items={actualites.map((actu) => ({
                            id: actu.id,
                            label: actu.title,
                            // Le détail saisi à la main prime sur la date : c'est lui qui
                            // apporte l'information, la date n'étant qu'un repère. Les
                            // entrées du flux RSS n'en portent jamais, la date reste alors
                            // seule visible.
                            sublabel: actu.body?.trim() || formatDate(actu.publishedAt),
                            url: actu.url,
                            // Un message avec détail montre sa date en badge (le détail
                            // occupe déjà le sous-titre) ; un billet RSS montre plutôt son
                            // flux d'origine, dans sa couleur, pour le distinguer d'un
                            // coup d'œil des autres sources.
                            badge: actu.body?.trim()
                                ? {
                                      text: formatDate(actu.publishedAt),
                                      className:
                                          'shrink-0 text-xs font-medium px-2 py-0.5 rounded-full bg-[var(--color-bg-hover)] text-[var(--color-text-secondary)]',
                                  }
                                : actu.feed
                                  ? {
                                        text: actu.feed.name,
                                        className: 'shrink-0 text-xs font-medium px-2 py-0.5 rounded-full',
                                        style: {
                                            backgroundColor: `${actu.feed.color}1a`,
                                            color: actu.feed.color,
                                        },
                                    }
                                  : null,
                        }))}
                    />
                </aside>
            )}
        </div>
    );
}

interface ShortcutPanelProps {
    title: string;
    icon: React.ReactNode;
    applications: Application[];
    onOpen: (application: Application) => void;
    /** Autorise le glisser-déposer des lignes pour changer leur ordre. */
    reorderable?: boolean;
    /** `beforeApplicationId` à `null` signifie « en fin de liste ». */
    onReorder?: (applicationId: string, beforeApplicationId: string | null) => void;
}

function ShortcutPanel({
    title,
    icon,
    applications,
    onOpen,
    reorderable = false,
    onReorder,
}: ShortcutPanelProps) {
    const [draggedId, setDraggedId] = useState<string | null>(null);

    /*
     * Glisser-déposer en HTML natif, sans bibliothèque : la liste ne dépasse
     * jamais cinq lignes, ce qui ne justifie pas la dépendance qu'imposerait
     * un réordonnancement plus ambitieux (listes longues, usage tactile fin).
     *
     * Déposer sur une ligne insère toujours juste avant elle, ce qui couvre
     * déjà toutes les positions sauf la dernière : glisser B avant C revient
     * à glisser B après A, pour toute ligne qui n'est pas la dernière. Seule
     * la dernière ligne a donc besoin de distinguer sa moitié haute (avant
     * elle) de sa moitié basse (en fin de liste), à la position du pointeur.
     */
    const handleDrop = (
        event: React.DragEvent<HTMLLIElement>,
        target: Application,
        isLast: boolean
    ) => {
        event.preventDefault();
        const sourceId = draggedId;
        setDraggedId(null);
        if (!sourceId || sourceId === target.id) return;

        if (isLast) {
            const rect = event.currentTarget.getBoundingClientRect();
            const deposeApres = event.clientY > rect.top + rect.height / 2;
            onReorder?.(sourceId, deposeApres ? null : target.id);
            return;
        }

        onReorder?.(sourceId, target.id);
    };

    return (
        <section className="card">
            <h2 className="flex items-center gap-2 px-4 py-3 text-sm font-semibold text-[var(--color-text-secondary)] border-b border-[var(--color-border)]">
                {icon}
                {title}
            </h2>
            <ul className="divide-y divide-[var(--color-border)]">
                {applications.map((application, index) => (
                    <li
                        key={application.id}
                        draggable={reorderable}
                        onDragStart={reorderable ? () => setDraggedId(application.id) : undefined}
                        onDragOver={reorderable ? (event) => event.preventDefault() : undefined}
                        onDrop={
                            reorderable
                                ? (event) =>
                                      handleDrop(event, application, index === applications.length - 1)
                                : undefined
                        }
                        onDragEnd={reorderable ? () => setDraggedId(null) : undefined}
                        className={cn(draggedId === application.id && 'opacity-40')}
                    >
                        <div className="flex items-center gap-3 px-4 py-2.5 hover:bg-[var(--color-bg-hover)] transition-colors">
                            {reorderable && (
                                <GripVertical
                                    className="w-4 h-4 text-[var(--color-text-muted)] cursor-grab shrink-0"
                                    aria-hidden
                                />
                            )}

                            <span
                                className="app-swatch flex items-center justify-center w-8 h-8 rounded-lg shrink-0"
                                style={appColor(application.color)}
                                aria-hidden
                            >
                                <AppIcon name={application.icon} className="w-4 h-4" />
                            </span>

                            <Link
                                to={`/applications/${application.slug}`}
                                className="flex-1 min-w-0 text-sm font-medium text-[var(--color-text)] truncate hover:text-[var(--color-primary)]"
                            >
                                {application.name}
                            </Link>

                            <button
                                type="button"
                                onClick={() => onOpen(application)}
                                className="btn btn-ghost p-1.5"
                                aria-label={`Ouvrir ${application.name}`}
                            >
                                <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    </li>
                ))}
            </ul>
        </section>
    );
}
